<template>
    <div v-if="historyOpen">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50"></div>
        <div class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div
                    class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                    <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                        <div class="flex items-center justify-between pb-4 border-b">
                            <h3 class="text-lg font-semibold">Project History</h3>
                            <button @click="closeHistoryModal" class="text-gray-400 hover:text-gray-500">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>

                        <div class="mt-4 space-y-4">
                            <div v-for="history in projectHistory" :key="history.id" class="border-b pb-4">
                                <div class="flex items-start justify-between">
                                    <div class="space-y-1">
                                        <div class="flex items-center space-x-2">
                                            <span class="text-sm font-medium" :class="{
                                                'text-orange-600': history.action_type === 'create',
                                                'text-orange-600': history.action_type === 'update',
                                                'text-purple-600': history.action_type === 'amount_update',
                                                'text-orange-600': history.action_type === 'add_project_amount',
                                                'text-red-600': history.action_type === 'delete'
                                            }">
                                                <i class="fas" :class="{
                                                    'fa-plus-circle': history.action_type === 'create',
                                                    'fa-edit': history.action_type === 'update',
                                                    'fa-money-bill': history.action_type === 'amount_update',
                                                    'fa-plus-square': history.action_type === 'add_project_amount',
                                                    'fa-trash': history.action_type === 'delete'
                                                }"></i>
                                                {{ formatActionType(history.action_type) }}
                                            </span>
                                            <span class="text-sm text-gray-500">by {{ history.creator?.name }}</span>
                                            <span v-if="history.modifier" class="text-sm text-gray-500">(modified by {{
                                                history.modifier?.name }})</span>
                                        </div>
                                        <div class="text-sm text-gray-500">
                                            {{ formatDate(history.created_at) }}
                                        </div>
                                    </div>
                                    <div class="text-sm">
                                        <status-level :status="history.status"></status-level>
                                    </div>
                                </div>

                                <div class="mt-2 text-sm">
                                    <div v-if="history.action_type === 'create'"
                                        class="space-y-2 bg-gray-50 p-4 rounded-lg">
                                        <p><span class="font-medium">Project Name:</span> {{ history.project_name }}</p>
                                        <p><span class="font-medium">Code:</span> {{ history.code }}</p>
                                        <p><span class="font-medium">Service Info:</span> {{ history.service_info?.name
                                        }}</p>
                                        <p><span class="font-medium">Description:</span> {{ history.description }}</p>
                                        <div class="grid grid-cols-2 gap-4">
                                            <div>
                                                <p><span class="font-medium">Project Amount:</span> {{
                                                    formatCurrency(history.project_amount) }}</p>
                                                <p><span class="font-medium">Remaining Amount:</span> {{
                                                    formatCurrency(history.remain_project_amount) }}</p>
                                            </div>
                                            <div>
                                                <p><span class="font-medium">MOOE Amount:</span> {{
                                                    formatCurrency(history.mooe_amount) }}</p>
                                                <p><span class="font-medium">CO Amount:</span> {{
                                                    formatCurrency(history.co_amount) }}</p>
                                            </div>
                                        </div>
                                        <p><span class="font-medium">Proposing Ministry:</span> {{
                                            history.proposing_ministry_name }}</p>
                                        <p><span class="font-medium">Implementing Ministry:</span> {{
                                            history.implementing_ministry_name }}</p>
                                        <p><span class="font-medium">Fiscal Year:</span> {{ history.fiscal_year }}</p>
                                        <p><span class="font-medium">Has Sub-projects:</span> {{ history.has_sub_project
                                            ? 'Yes' : 'No' }}</p>
                                    </div>

                                    <div v-else-if="history.action_type === 'add_project_amount'"
                                        class="space-y-2 bg-gray-50 p-4 rounded-lg">
                                        <div class="grid grid-cols-2 gap-4">
                                            <div class="space-y-1">
                                                <p class="font-medium text-gray-700">Previous Project Details:</p>
                                                <p><span class="font-medium">Project Amount:</span> {{
                                                    formatCurrency(history.changes?.project_amount?.old) }}</p>
                                                <p><span class="font-medium">Remaining Amount:</span> {{
                                                    formatCurrency(history.changes?.remain_project_amount?.old) }}</p>
                                                <p><span class="font-medium">MOOE Amount:</span> {{
                                                    formatCurrency(history.changes?.mooe_amount?.old) }}</p>
                                                <p><span class="font-medium">CO Amount:</span> {{
                                                    formatCurrency(history.changes?.co_amount?.old) }}</p>
                                            </div>
                                            <div class="space-y-1">
                                                <p class="font-medium text-gray-700">Amount Added:</p>
                                                <p><span class="font-medium">Additional Amount:</span>
                                                    {{ formatCurrency(
                                                        (history.changes?.project_amount?.new || 0) -
                                                        (history.changes?.project_amount?.old || 0)
                                                    ) }}
                                                </p>
                                                <p class="font-medium text-gray-700 mt-4">New Totals:</p>
                                                <p><span class="font-medium">Project Amount:</span> {{
                                                    formatCurrency(history.changes?.project_amount?.new) }}</p>
                                                <p><span class="font-medium">Remaining Amount:</span> {{
                                                    formatCurrency(history.changes?.remain_project_amount?.new) }}</p>
                                                <p><span class="font-medium">MOOE Amount:</span> {{
                                                    formatCurrency(history.changes?.mooe_amount?.new) }}</p>
                                                <p><span class="font-medium">CO Amount:</span> {{
                                                    formatCurrency(history.changes?.co_amount?.new) }}</p>
                                            </div>
                                        </div>
                                        <div class="mt-4 text-gray-600">
                                            <p v-if="history.changes?.remarks"><span class="font-medium">Remarks:</span>
                                                {{ history.changes.remarks }}</p>
                                        </div>
                                    </div>

                                    <div v-else-if="history.action_type === 'amount_update'"
                                        class="space-y-2 bg-gray-50 p-4 rounded-lg">
                                        <div class="grid grid-cols-2 gap-4">
                                            <div class="space-y-1">
                                                <p class="font-medium text-gray-700">Previous Amounts:</p>
                                                <p><span class="font-medium">Project Amount:</span> {{
                                                    formatCurrency(history.changes?.project_amount?.old) }}</p>
                                                <p><span class="font-medium">Remaining Amount:</span> {{
                                                    formatCurrency(history.changes?.remain_project_amount?.old) }}</p>
                                                <p><span class="font-medium">MOOE Amount:</span> {{
                                                    formatCurrency(history.changes?.mooe_amount?.old) }}</p>
                                                <p><span class="font-medium">CO Amount:</span> {{
                                                    formatCurrency(history.changes?.co_amount?.old) }}</p>
                                            </div>
                                            <div class="space-y-1">
                                                <p class="font-medium text-gray-700">New Amounts:</p>
                                                <p><span class="font-medium">Project Amount:</span> {{
                                                    formatCurrency(history.changes?.project_amount?.new) }}</p>
                                                <p><span class="font-medium">Remaining Amount:</span> {{
                                                    formatCurrency(history.changes?.remain_project_amount?.new) }}</p>
                                                <p><span class="font-medium">MOOE Amount:</span> {{
                                                    formatCurrency(history.changes?.mooe_amount?.new) }}</p>
                                                <p><span class="font-medium">CO Amount:</span> {{
                                                    formatCurrency(history.changes?.co_amount?.new) }}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-else class="space-y-2 bg-gray-50 p-4 rounded-lg">
                                        <div v-for="(change, field) in history.changes" :key="field"
                                            class="text-gray-600">
                                            <div class="flex items-start">
                                                <span class="font-medium w-1/4">{{ formatFieldName(field) }}:</span>
                                                <div class="flex-1">
                                                    <div class="line-through text-red-500">{{ formatValue(change.old) }}
                                                    </div>
                                                    <div class="text-orange-500">{{ formatValue(change.new) }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="projectHistory.length === 0" class="text-center py-4 text-gray-500">
                                <i class="fas fa-history mr-2"></i>No history found for this project
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        Hello, this is the project history modal for project ID:
        {{ projectId }}
        {{ historyOpen }}
    </div>
</template>

<script>
    import axios from "axios"
    import { ref, watch } from 'vue'
    import moment from 'moment'
    import StatusLevel from '@/Shared/StatusLevel'

    export default {
        components: {
            StatusLevel
        },

        props: {
            projectId: {
                type: Number,
                required: true
            },
            historyOpen: {
                type: Boolean,
                default: false
            }
        },

        emits: ['updateHistoryPopup'],

        setup(props, { emit }) {
            const projectHistory = ref([])

            const closeHistoryModal = () => {
                emit('updateHistoryPopup', false)
            }

            const formatDate = (date) => {
                return moment(date).format('MMMM D, YYYY h:mm A')
            }

            const formatActionType = (type) => {
                switch (type) {
                    case 'create':
                        return 'Created'
                    case 'update':
                        return 'Updated'
                    case 'amount_update':
                        return 'Amount Updated'
                    case 'add_project_amount':
                        return 'Amount Added'
                    case 'delete':
                        return 'Deleted'
                    default:
                        return type
                }
            }

            const formatFieldName = (field) => {
                return field
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            }

            const formatCurrency = (amount) => {
                if (amount === null || amount === undefined) return 'N/A'
                return new Intl.NumberFormat('en-PH', {
                    style: 'currency',
                    currency: 'PHP'
                }).format(amount)
            }

            const formatValue = (value) => {
                if (value === null || value === undefined) return 'N/A'
                if (typeof value === 'boolean') return value ? 'Yes' : 'No'
                if (typeof value === 'object') return JSON.stringify(value)
                if (typeof value === 'number' && String(value).includes('.')) return formatCurrency(value)
                return value
            }

            watch(() => props.historyOpen, async (newVal) => {
                if (newVal && props.projectId) {
                    try {
                        const response = await axios.get(`/backend/projects/${props.projectId}/history`)
                        projectHistory.value = response.data
                    } catch (error) {
                        console.error('Error fetching project history:', error)
                    }
                }
            })

            return {
                projectHistory,
                closeHistoryModal,
                formatDate,
                formatActionType,
                formatFieldName,
                formatCurrency,
                formatValue
            }
        }
    }
</script>
